import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Col, Label, Input, Alert, Table, Row, FormGroup } from 'reactstrap';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { useShortenPcbSn } from './hooks/useShortenPcbSn';

export function RomanakPlotView() {
    const history = useHistory();
    const location = useLocation();

    const { selectedBoards = [], pairs = [] } = location.state || {};

    const [fromTimestamp, setFromTimestamp] = useState(0);
    const [toTimestamp, setToTimestamp] = useState(0);

    const [graphData, setGraphData] = useState({});   // Single data state only

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [activeSeries, setActiveSeries] = useState(new Set());

    // Axis tick count sliders
    const [xAxisTicks, setXAxisTicks] = useState(12);
    const [yAxisTicks, setYAxisTicks] = useState(12);

    // Constrain data checkbox
    const [constrainData, setConstrainData] = useState(false);

    // UTC time picker state
    const [fromYear, setFromYear] = useState('');
    const [fromMon, setFromMon] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [fromHrs, setFromHrs] = useState('');
    const [fromMin, setFromMin] = useState('');
    const [fromSec, setFromSec] = useState('');

    const [toYear, setToYear] = useState('');
    const [toMon, setToMon] = useState('');
    const [toDate, setToDate] = useState('');
    const [toHrs, setToHrs] = useState('');
    const [toMin, setToMin] = useState('');
    const [toSec, setToSec] = useState('');

    const hasInitialLoaded = useRef(false);
    const token = localStorage.getItem("token");

    const deviceIds = [...new Set(selectedBoards.map(b => b.idImpDevice).filter(Boolean))];

    const { shortenPcbSn } = useShortenPcbSn();

    const allSeriesKeys = [];
    selectedBoards.forEach(board => {
        const shortSn = shortenPcbSn(board.pcbSn);
        pairs.forEach((pair) => {
            if (pair.x && pair.y && pair.x !== pair.y) {
                const key = `${shortSn}_${pair.y}_vs_${pair.x}`;
                allSeriesKeys.push(key);
            }
        });
    });

    useEffect(() => {
        const initialActive = new Set(allSeriesKeys);
        setActiveSeries(initialActive);
    }, [selectedBoards, pairs]);

    // Initial time range from most recent data
    useEffect(() => {
        if (deviceIds.length === 0 || hasInitialLoaded.current) return;
        hasInitialLoaded.current = true;

        const fetchMostRecentData = async () => {
            try {
                const recentData = await Promise.all(
                    selectedBoards.map(async (board) => {
                        const resp = await fetch(`api/boardData/pcbSn/${board.pcbSn}/1`, {
                            method: "GET",
                            headers: { "Authorization": `Bearer ${token}` }
                        });
                        if (resp.ok) return (await resp.json())[0];
                        return null;
                    })
                );

                let latest = 0;
                recentData.forEach(r => {
                    if (r?.timeReceived) {
                        let ts = typeof r.timeReceived === 'string'
                            ? new Date(r.timeReceived).getTime() / 1000
                            : Number(r.timeReceived);
                        ts = Math.round(ts);
                        if (ts > latest) latest = ts;
                    }
                });

                if (latest === 0) latest = Math.round(Date.now() / 1000);
                const fromTime = latest - (15 * 86400);

                setToTimestamp(latest);
                setFromTimestamp(fromTime);
                populateTimeFields(fromTime, latest);
            } catch (err) {
                const now = Math.round(Date.now() / 1000);
                setToTimestamp(now);
                setFromTimestamp(now - 15 * 86400);
                populateTimeFields(now - 15 * 86400, now);
            }
        };

        fetchMostRecentData();
    }, [deviceIds, selectedBoards, token]);

    const populateTimeFields = (fromTs, toTs) => {
        const fromObj = new Date(fromTs * 1000);
        setFromYear(fromObj.getUTCFullYear());
        setFromMon(fromObj.getUTCMonth() + 1);
        setFromDate(fromObj.getUTCDate());
        setFromHrs(fromObj.getUTCHours());
        setFromMin(fromObj.getUTCMinutes());
        setFromSec(fromObj.getUTCSeconds());

        const toObj = new Date(toTs * 1000);
        setToYear(toObj.getUTCFullYear());
        setToMon(toObj.getUTCMonth() + 1);
        setToDate(toObj.getUTCDate());
        setToHrs(toObj.getUTCHours());
        setToMin(toObj.getUTCMinutes());
        setToSec(toObj.getUTCSeconds());
    };

    const getTimestampsFromInputs = () => {
        const fromTs = Math.floor(Date.UTC(parseInt(fromYear), parseInt(fromMon) - 1, parseInt(fromDate),
            parseInt(fromHrs), parseInt(fromMin), parseInt(fromSec)) / 1000);
        const toTs = Math.floor(Date.UTC(parseInt(toYear), parseInt(toMon) - 1, parseInt(toDate),
            parseInt(toHrs), parseInt(toMin), parseInt(toSec)) / 1000);
        return { fromTs, toTs };
    };

    const handleRefresh = () => {
        const { fromTs, toTs } = getTimestampsFromInputs();
        if (isNaN(fromTs) || isNaN(toTs) || toTs <= fromTs) {
            setError("Invalid time range. 'To' must be after 'From'.");
            return;
        }

        setFromTimestamp(fromTs);
        setToTimestamp(toTs);
        setError(null);
        setLoading(true);
    };

    useEffect(() => {
        if (fromTimestamp > 0 && toTimestamp > 0 && toTimestamp > fromTimestamp) {
            loadGraphData();
        }
    }, [fromTimestamp, toTimestamp]);

    const loadGraphData = async () => {
        if (deviceIds.length === 0) {
            setError("No valid Device IDs found.");
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        setGraphData({});          // ← Clear data IMMEDIATELY (prevents old data flash)

        try {
            const resp = await fetch(
                `api/boarddata/limits?notbefore=${Math.floor(fromTimestamp)}&until=${Math.floor(toTimestamp)}&offset=0&limit=10100`,
                {
                    method: 'POST',
                    body: JSON.stringify(deviceIds),
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-type": "application/json"
                    }
                }
            );

            if (!resp.ok) throw new Error(`HTTP error! status: ${resp.status}`);

            const rawData = await resp.json();
            const seriesData = {};

            rawData.forEach(entry => {
                const shortSn = shortenPcbSn(entry.pcbSn);
                if (!shortSn) return;

                let timestamp = entry.timeReceived || entry.timeSent;
                if (typeof timestamp === 'string') {
                    timestamp = new Date(timestamp).getTime();
                } else {
                    timestamp = Number(timestamp) * 1000;
                }

                pairs.forEach((pair) => {
                    if (!pair.x || !pair.y || pair.x === pair.y) return;

                    const seriesKey = `${shortSn}_${pair.y}_vs_${pair.x}`;
                    if (!activeSeries.has(seriesKey)) return;

                    const xVal = Number(entry.dataList?.[pair.x]);
                    const yVal = Number(entry.dataList?.[pair.y]);

                    if (isNaN(xVal) || isNaN(yVal) || !isFinite(xVal) || !isFinite(yVal)) return;

                    if (!seriesData[seriesKey]) seriesData[seriesKey] = [];
                    seriesData[seriesKey].push({
                        x: xVal,
                        y: yVal,
                        timestamp: timestamp,
                        pcbSn: shortSn,
                        xField: pair.x,
                        yField: pair.y
                    });
                });
            });

            setGraphData(seriesData);

        } catch (err) {
            setError(err.message || "Failed to load graph data.");
            setGraphData({});          // Clear on error
        } finally {
            setLoading(false);
        }
    };

    const toggleSeries = (seriesKey) => {
        setActiveSeries(prev => {
            const newSet = new Set(prev);
            if (newSet.has(seriesKey)) newSet.delete(seriesKey);
            else newSet.add(seriesKey);
            return newSet;
        });
    };

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            const date = new Date(data.timestamp);
            return (
                <div className="custom-tooltip" style={{
                    backgroundColor: 'white',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                    <p><strong>X:</strong> {data.x} ({data.xField})</p>
                    <p><strong>Y:</strong> {data.y} ({data.yField})</p>
                    <p><strong>Timestamp:</strong> {date.toLocaleString('en-US')}</p>
                    <p><strong>PcbSn:</strong> {data.pcbSn}</p>
                </div>
            );
        }
        return null;
    };

    if (selectedBoards.length === 0 || pairs.length === 0) {
        return (
            <Col sm="12" md={{ size: 8, offset: 2 }}>
                <Alert color="danger">
                    No boards or X-Y pairs selected.
                    <Button color="link" onClick={() => history.goBack()}>Go Back</Button>
                </Alert>
            </Col>
        );
    }

    return (
        <Col sm="12" className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="mb-0">Romanak Plot View</h2>
                <div>
                    <Button color="primary" onClick={handleRefresh} disabled={loading} className="me-2">
                        {loading ? "Loading Data..." : "Refresh Plot"}
                    </Button>
                    <Button color="secondary" onClick={() => history.goBack()}>
                        Back to Selection
                    </Button>
                </div>
            </div>

            {/* Selected Boards & Pairs Table */}
            <div className="mb-4">
                <h5>Selected Boards &amp; X-Y Pairs — Click a cell to toggle the series</h5>
                <Table bordered size="sm" className="data-fields-table">
                    <thead>
                        <tr>
                            <th style={{ width: '180px' }}>Board (PcbSn)</th>
                            {pairs.map((pair, idx) => (
                                pair.x && pair.y && pair.x !== pair.y && (
                                    <th key={idx}>
                                        Pair {idx + 1}<br />
                                        <small>{pair.y} vs {pair.x}</small>
                                    </th>
                                )
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {selectedBoards.map(board => {
                            const shortSn = shortenPcbSn(board.pcbSn);
                            return (
                                <tr key={board.id}>
                                    <td><span className="code">{shortSn}</span></td>
                                    {pairs.map((pair, idx) => {
                                        if (!pair.x || !pair.y || pair.x === pair.y) return <td key={idx}></td>;
                                        const seriesKey = `${shortSn}_${pair.y}_vs_${pair.x}`;
                                        const isActive = activeSeries.has(seriesKey);
                                        return (
                                            <td
                                                key={idx}
                                                style={{
                                                    textAlign: 'center',
                                                    cursor: 'pointer',
                                                    backgroundColor: isActive ? '#cfe2ff' : '#f8f9fa',
                                                    fontWeight: isActive ? '600' : 'normal'
                                                }}
                                                onClick={() => toggleSeries(seriesKey)}
                                            >
                                                {isActive ? '✓' : ' '}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>

            {/* Graph Axis Density */}
            <div className="mb-4">
                <h5>Graph Axis Density</h5>
                <Row className="align-items-center">
                    <Col md={4}>
                        <Label className="mb-2">Y-Axis Ticks: <strong>{yAxisTicks}</strong></Label>
                        <input type="range" min="5" max="32" step="1" value={yAxisTicks} onChange={e => setYAxisTicks(parseInt(e.target.value))} className="form-range" />
                    </Col>
                    <Col md={4}>
                        <Label className="mb-2">X-Axis Ticks: <strong>{xAxisTicks}</strong></Label>
                        <input type="range" min="5" max="32" step="1" value={xAxisTicks} onChange={e => setXAxisTicks(parseInt(e.target.value))} className="form-range" />
                    </Col>
                    <Col md={4}>
                        <FormGroup check>
                            <Label check>
                                <Input type="checkbox" checked={constrainData} onChange={e => setConstrainData(e.target.checked)} />{' '}
                                Constrain data to (0...25%)
                            </Label>
                        </FormGroup>
                    </Col>
                </Row>
            </div>

            {/* Time Range Controls */}
            <div className="mb-4">
                <Row className="align-items-start">
                    <Col md={6}>
                        <Label className="mb-2"><strong>From (UTC)</strong></Label>
                        <div className="d-flex gap-2 flex-wrap">
                            <div><div className="small text-muted mb-1">Year</div><Input type="text" value={fromYear} onChange={e => setFromYear(e.target.value)} maxLength={4} style={{ width: '80px' }} /></div>
                            <div><div className="small text-muted mb-1">Month</div><Input type="text" value={fromMon} onChange={e => setFromMon(e.target.value)} maxLength={2} style={{ width: '60px' }} /></div>
                            <div><div className="small text-muted mb-1">Day</div><Input type="text" value={fromDate} onChange={e => setFromDate(e.target.value)} maxLength={2} style={{ width: '60px' }} /></div>
                            <div><div className="small text-muted mb-1">Hr</div><Input type="text" value={fromHrs} onChange={e => setFromHrs(e.target.value)} maxLength={2} style={{ width: '60px' }} /></div>
                            <div><div className="small text-muted mb-1">Min</div><Input type="text" value={fromMin} onChange={e => setFromMin(e.target.value)} maxLength={2} style={{ width: '60px' }} /></div>
                            <div><div className="small text-muted mb-1">Sec</div><Input type="text" value={fromSec} onChange={e => setFromSec(e.target.value)} maxLength={2} style={{ width: '60px' }} /></div>
                        </div>
                    </Col>
                    <Col md={6}>
                        <Label className="mb-2"><strong>To (UTC)</strong></Label>
                        <div className="d-flex gap-2 flex-wrap">
                            <div><div className="small text-muted mb-1">Year</div><Input type="text" value={toYear} onChange={e => setToYear(e.target.value)} maxLength={4} style={{ width: '80px' }} /></div>
                            <div><div className="small text-muted mb-1">Month</div><Input type="text" value={toMon} onChange={e => setToMon(e.target.value)} maxLength={2} style={{ width: '60px' }} /></div>
                            <div><div className="small text-muted mb-1">Day</div><Input type="text" value={toDate} onChange={e => setToDate(e.target.value)} maxLength={2} style={{ width: '60px' }} /></div>
                            <div><div className="small text-muted mb-1">Hr</div><Input type="text" value={toHrs} onChange={e => setToHrs(e.target.value)} maxLength={2} style={{ width: '60px' }} /></div>
                            <div><div className="small text-muted mb-1">Min</div><Input type="text" value={toMin} onChange={e => setToMin(e.target.value)} maxLength={2} style={{ width: '60px' }} /></div>
                            <div><div className="small text-muted mb-1">Sec</div><Input type="text" value={toSec} onChange={e => setToSec(e.target.value)} maxLength={2} style={{ width: '60px' }} /></div>
                        </div>
                    </Col>
                </Row>
            </div>

            {error && <Alert color="danger" className="mt-3">{error}</Alert>}

            {/* Romanak Plot */}
            {Object.keys(graphData).length > 0 && !loading ? (
                <div className="mb-5">
                    <h5>Romanak Plot — Click cells above to toggle series</h5>
                    <ResponsiveContainer width={800} height={800}>
                        <ScatterChart margin={{ top: 20, right: -400, left: 20, bottom: 80 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" dataKey="x" name="X" domain={[0, 250000]} allowDataOverflow={constrainData} tickCount={xAxisTicks} angle={-45} textAnchor="end" height={80} tick={{ fontSize: 13 }} label={{ value: 'CO2 ppm', position: 'bottom', offset: -18 }} />
                            <YAxis type="number" dataKey="y" name="Y" domain={[0, 250000]} allowDataOverflow={constrainData} tickCount={yAxisTicks} tick={{ fontSize: 13 }} label={{ value: 'O2 ppm', angle: -90, position: 'left' }} />
                            <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />
                            <Legend layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{ paddingLeft: '30px' }} />

                            <ReferenceLine segment={[{ x: 0, y: 210000 }, { x: 210000, y: 0 }]} stroke="#000000" strokeDasharray="4 4" strokeWidth={2} />
                            <ReferenceLine segment={[{ x: 0, y: 216300 }, { x: 216300, y: 0 }]} stroke="#ff0000" strokeDasharray="4 4" strokeWidth={2} />
                            <ReferenceLine segment={[{ x: 0, y: 210000 }, { x: 105000, y: 0 }]} stroke="#000000" strokeDasharray="4 4" strokeWidth={2} />

                            {allSeriesKeys.filter(key => activeSeries.has(key)).map((seriesKey, i) => {
                                const points = graphData[seriesKey] || [];
                                return <Scatter key={seriesKey} name={seriesKey} data={points} fill={`hsl(${i * 45}, 80%, 55%)`} shape="circle" />;
                            })}
                        </ScatterChart>
                    </ResponsiveContainer>
                </div>
            ) : loading ? (
                <Alert color="info" className="mt-4">
                    <strong>Loading new data for the selected time range...</strong><br />
                    <small>This may take 30 seconds or more depending on the date range.</small>
                </Alert>
            ) : (
                <Alert color="warning" className="mt-3">
                    No matching X-Y data found in the selected time range.
                </Alert>
            )}
        </Col>
    );
}